/**
 * Admin 「외부 연동」 화면 — styled(페이지 로컬).
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 */
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";

export const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  max-width: 1400px;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

export const BackLink = styled.a`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const EnvBadgeBox = styled.span<{ $rel: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.$rel ? COLOR_SYSTEM.get("Gray")?.[0] : COLOR_SYSTEM.get("Gray")?.[700])};
  background-color: ${(p) => (p.$rel ? COLOR_SYSTEM.get("Red")?.[400] : COLOR_SYSTEM.get("Gray")?.[50])};
  border: 1px solid ${(p) => (p.$rel ? COLOR_SYSTEM.get("Red")?.[500] : COLOR_SYSTEM.get("Gray")?.[200])};
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 12px;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

export const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-weight: 600;
  white-space: nowrap;
`;

export const Tr = styled.tr<{ $clickable?: boolean; $muted?: boolean }>`
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")};
  color: ${(p) => (p.$muted ? COLOR_SYSTEM.get("Gray")?.[400] : "inherit")};
  &:hover {
    background-color: ${(p) => (p.$clickable ? COLOR_SYSTEM.get("Gray")?.[50] : "transparent")};
  }
`;

export const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  color: inherit;
  vertical-align: middle;
`;

export const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
`;

export const Muted = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

export const Warn = styled.span`
  color: ${COLOR_SYSTEM.get("Red")?.[500]};
  font-weight: 600;
`;

export const StatusText = styled.span<{ $on: boolean }>`
  font-weight: 600;
  color: ${(p) => (p.$on ? COLOR_SYSTEM.get("Green")?.[600] : COLOR_SYSTEM.get("Gray")?.[500])};
`;

export const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 8px 16px;
  margin: 0;
  font-size: 14px;
  dt {
    color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  }
  dd {
    margin: 0;
    color: ${COLOR_SYSTEM.get("Gray")?.[800]};
    word-break: break-all;
  }
`;

export const Guide = styled.ol`
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  line-height: 1.6;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

export const Notice = styled.div<{ $tone?: "warn" | "info" }>`
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 8px;
  color: ${(p) => (p.$tone === "warn" ? COLOR_SYSTEM.get("Red")?.[600] : COLOR_SYSTEM.get("Gray")?.[700])};
  background-color: ${(p) => (p.$tone === "warn" ? COLOR_SYSTEM.get("Red")?.[50] : COLOR_SYSTEM.get("Gray")?.[50])};
`;

export const EmptyText = styled.div`
  padding: 16px 0;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

// ---------------------------------------------------------------------------
// 모달 본문 (ConfirmManager body)
// ---------------------------------------------------------------------------

export const ModalBody = styled.div`
  width: 520px;
  max-width: 80vw;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 20px;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const FieldLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

export const ReadOnlyValue = styled.span`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

export const Hint = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

export const ErrorMsg = styled.span`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Red")?.[500]};
`;

export const TextInput = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  font-size: 14px;
`;

export const TextArea = styled.textarea`
  min-height: 84px;
  padding: 8px 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  font-size: 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  resize: vertical;
`;

export const Select = styled.select`
  height: 36px;
  padding: 0 8px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

export const CheckLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
  cursor: pointer;
`;

export const ModalFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 8px;
`;

export const RawBox = styled.div`
  padding: 12px;
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-all;
  user-select: all;
`;
